import { API } from 'aws-amplify';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode 
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === 'DEV' ? "Node-API" : "prod-api");

// Get an existing probation review by employee email
export const getProbationReview = async (email, id) => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getPerformanceReview',
        email,
        reviewType: 'probation',
        ...(id ? { id } : {}),
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Get all probation reviews
export const getAllProbationReviews = async () => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getAllPerformanceReviews',
        reviewType: 'probation',
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return { reviews: [] };
  } catch (error) {
    console.error('Error getting all probation reviews:', error);
    return { reviews: [] };
  }
};

// Create a new probation review row for the employee (self review flow)
const addProbationReview = async ({ email, user_response, periodStart, periodEnd, reviewPeriod, formId }) => {
  const now = new Date().toISOString();
  const payload = {
    command: 'addPerformanceReview',
    id: `${email}_probation_${periodStart}`, // unique id per probation period
    email,
    reviewType: 'probation',
    form_id: formId || '',
    user_response,
    manager_response: '',
    second_level_manager_response: '',
    period_start: periodStart,
    period_end: periodEnd,
    review_period: reviewPeriod, // e.g., "Month 1-3" or "Month 4-6"
    created_at: now,
    updated_at: now,
  };
  return API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
};

// Update only the user_response JSON string
const updateUserProbationResponse = async ({ id, email, user_response }) => {
  const payload = {
    command: 'updateUserPerformanceResponse',
    id,
    email,
    reviewType: 'probation',
    user_response,
    updated_at: new Date().toISOString(),
  };
  return API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
};

// Public API: Save self probation review as JSON in user_response
export const saveSelfProbationReview = async (inputs) => {
  try {
    const email = inputs.userEmail;
    const periodStart = inputs.periodStart;
    const periodEnd = inputs.periodEnd;
    const reviewPeriod = inputs.reviewPeriod;
    const formId = inputs.formId || '';
    
    // Use responseData if provided (includes schema), otherwise stringify all inputs
    const user_response = inputs.responseData 
      ? JSON.stringify(inputs.responseData)
      : JSON.stringify(inputs);
    
    const id = `${email}_probation_${periodStart}`;
    const existing = await getProbationReview(email, id);
    
    if (!existing || !existing.review) {
      return await addProbationReview({ email, user_response, periodStart, periodEnd, reviewPeriod, formId });
    }
    
    return await updateUserProbationResponse({ id, email, user_response });
  } catch (error) {
    console.error('Error saving self probation review:', error);
    throw error;
  }
};

// Update only the manager_response JSON string for the selected employee
export const saveManagerProbationReview = async (inputs) => {
  try {
    const email = inputs.employeeEmail;
    
    // Use responseData if provided (includes schema), otherwise stringify all inputs
    const manager_response = inputs.responseData 
      ? JSON.stringify(inputs.responseData)
      : JSON.stringify(inputs);
      
    const payload = {
      command: 'updateManagerPerformanceResponse',
      id: inputs.employeeId || inputs.id || email,
      email,
      reviewType: 'probation',
      manager_response,
      updated_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
    return response;
  } catch (error) {
    console.error('Error saving manager probation review:', error);
    throw error;
  }
};

// Update only the second_level_manager_response JSON string
export const saveSecondLevelManagerProbationReview = async (inputs) => {
  try {
    const email = inputs.employeeEmail;
    const second_level_manager_response = JSON.stringify(inputs);
    const payload = {
      command: 'updateSecondLevelManagerPerformanceResponse',
      id: inputs.employeeId || inputs.id || email,
      email,
      reviewType: 'probation',
      second_level_manager_response,
      updated_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
    return response;
  } catch (error) {
    console.error('Error saving second level manager probation review:', error);
    throw error;
  }
};

// Create period configuration for probation reviews
export const createProbationPeriod = async (periodConfig) => {
  try {
    const payload = {
      command: 'createProbationPeriod',
      ...periodConfig,
      created_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
    return response;
  } catch (error) {
    console.error('Error creating probation period:', error);
    throw error;
  }
};

// Get probation period configurations
export const getProbationPeriods = async (email) => {
  try {
    const res = await API.post(api_name, '', {
      body: {
        command: 'getProbationPeriods',
        email,
      },
      response: true,
      convertEmptyValues: true,
      headers: {},
    });
    if (res?.status === 200 && res?.data) {
      try { return JSON.parse(res.data.body); } catch { return res.data; }
    }
    return { periods: [] };
  } catch (error) {
    console.error('Error getting probation periods:', error);
    return { periods: [] };
  }
};

// Duplicate an existing probation review for a new period
export const duplicateProbationReview = async (sourceId, newPeriodStart, newPeriodEnd, reviewPeriod) => {
  try {
    const payload = {
      command: 'duplicateProbationReview',
      sourceId,
      newPeriodStart,
      newPeriodEnd,
      reviewPeriod,
      created_at: new Date().toISOString(),
    };
    const response = await API.post(api_name, '', { body: payload, response: true, convertEmptyValues: true, headers: {} });
    return response;
  } catch (error) {
    console.error('Error duplicating probation review:', error);
    throw error;
  }
};
